import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowUpRight, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { revealUp } from '../lib/anim';



const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


const CONTACT = {
  form: "contact-form",
  name: "contact-name",
  email: "contact-email",
  message: "contact-message",
  submit: "contact-submit",
};



const schema = z.object({
  name: z.string().min(2, 'Please enter your name'),
  email: z.string().email('Enter a valid email'),
  subject: z.string().optional(),
  message: z.string().min(10, 'Tell me a little more (min 10 chars)'),
  website: z.string().optional(),
});

const Field = ({ label, error, testId, children }) => (
  <div className="border-b border-border py-5 transition-colors focus-within:border-foreground">
    <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
      {label}
    </label>
    {children}
    {error && (
      <p className="mt-2 font-mono text-[11px] text-destructive-foreground/90">
        {error}
      </p>
    )}
  </div>
);

const inputCls =
  'w-full bg-transparent font-display text-xl text-foreground placeholder:text-muted-foreground/40 outline-none md:text-2xl';

export default function Contact() {
  const [sent, setSent] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: zodResolver(schema) });

  const onSubmit = async (data) => {
    try {
      await axios.post(`${API}/contact`, data);
      setSent(true);
      reset();
      toast.success('Message sent — I’ll be in touch soon.');
    } catch (e) {
      toast.error('Something went wrong. Please try again.');
    }
  };

  return (
    <section
      id="contact"
      className="relative mx-auto max-w-[1600px] px-6 py-24 md:px-12 md:py-40"
    >
      <div className="grid grid-cols-1 gap-16 md:grid-cols-12">
        <div className="md:col-span-5">
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">
            (Contact)
          </span>
          <motion.h2
            variants={revealUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.5 }}
            className="mt-6 font-display text-6xl font-extrabold leading-[0.85] tracking-tighter text-foreground md:text-8xl"
          >
            Let&apos;s
            <br />
            talk.
          </motion.h2>
          <p className="mt-8 max-w-sm text-base leading-relaxed text-muted-foreground">
            Have a project, a role, or just an idea worth exploring? I&apos;m
            currently open to select freelance and full-time work.
          </p>
          <a
            href="mailto:hello@michelerago.design"
            className="mt-6 inline-block font-mono text-sm text-foreground underline underline-offset-4 transition-opacity hover:opacity-60"
            data-testid="contact-email-link"
          >
            ragomichele716@gmail.com
          </a>
        </div>

        <div className="md:col-span-7">
          <AnimatePresence mode="wait">
            {sent ? (
              <motion.div
                key="success"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                data-testid={CONTACT.success}
                className="flex h-full min-h-[300px] flex-col items-start justify-center"
              >
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground">
                  <Check className="h-6 w-6" />
                </div>
                <h3 className="mt-6 font-display text-3xl font-medium text-foreground">
                  Message received.
                </h3>
                <p className="mt-2 text-muted-foreground">
                  Thanks for reaching out — expect a reply within 48 hours.
                </p>
                <button
                  onClick={() => setSent(false)}
                  className="mt-6 font-mono text-xs uppercase tracking-[0.15em] text-muted-foreground underline underline-offset-4 hover:text-foreground"
                >
                  Send another
                </button>
              </motion.div>
            ) : (
              <motion.form
                key="form"
                data-testid={CONTACT.form}
                onSubmit={handleSubmit(onSubmit)}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="w-full"
                noValidate
              >
                {/* Honeypot — hidden from humans, catches bots */}
                <div
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    left: '-9999px',
                    width: '1px',
                    height: '1px',
                    overflow: 'hidden',
                  }}
                >
                  <label>
                    Website
                    <input
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      data-testid="contact-input-website"
                      {...register('website')}
                    />
                  </label>
                </div>
                <div className="grid grid-cols-1 gap-x-8 md:grid-cols-2">
                  <Field label="Your name" error={errors.name?.message}>
                    <input
                      data-testid={CONTACT.name}
                      className={inputCls}
                      placeholder="Jane Doe"
                      {...register('name')}
                    />
                  </Field>
                  <Field label="Email" error={errors.email?.message}>
                    <input
                      data-testid={CONTACT.email}
                      className={inputCls}
                      placeholder="jane@studio.com"
                      {...register('email')}
                    />
                  </Field>
                </div>
                <Field label="Subject" error={errors.subject?.message}>
                  <input
                    data-testid={CONTACT.subject}
                    className={inputCls}
                    placeholder="New product design"
                    {...register('subject')}
                  />
                </Field>
                <Field label="Message" error={errors.message?.message}>
                  <textarea
                    data-testid={CONTACT.message}
                    rows={3}
                    className={`${inputCls} resize-none`}
                    placeholder="Tell me about your project…"
                    {...register('message')}
                  />
                </Field>

                <button
                  type="submit"
                  data-testid={CONTACT.submit}
                  disabled={isSubmitting}
                  className="group mt-10 flex w-full items-center justify-between rounded-full bg-primary px-8 py-5 font-mono text-sm uppercase tracking-[0.15em] text-primary-foreground transition-transform duration-300 active:scale-[0.98] disabled:opacity-60 md:w-auto md:gap-16"
                >
                  {isSubmitting ? 'Sending…' : 'Send message'}
                  {isSubmitting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  )}
                </button>
              </motion.form>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
